"use client";

import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Calculator, CalendarDays, BookmarkPlus } from "lucide-react";
import posthog from "posthog-js";
import Link from "next/link";

import { CalculatorForm } from "@/ui/components/calculator/calculator-form";
import { TitrationPlanner } from "@/ui/components/calculator/titration-planner";
import { SavedProtocols } from "@/ui/components/calculator/saved-protocols";
import { BuyBridgeGate, computeBuyBridgePerDoseLabel } from "@/ui/components/calculator/buy-bridge-card";
import { Cockpit, accentClassFor } from "@/ui/components/calculator/cockpit";
import { ModeToggle } from "@/ui/components/calculator/mode-toggle";
import { SafetyNotes } from "@/ui/components/calculator/safety-notes";
import { StickyPill } from "@/ui/components/calculator/sticky-pill";
import { PeptidePicker } from "@/ui/components/calculator/peptide-picker";
import { CalculatorActions } from "@/ui/components/calculator/calculator-actions";
import { InternalSourceLink } from "@/ui/components/analytics/internal-source-link";
import { ResultProductLink } from "@/ui/components/calculator/result-product-link";
import { HistoryStrip, type HistoryStripRestore } from "@/ui/components/calculator/history-strip";
import { isFiveProductLaunchSlug, isFiveProductStudyContextSlug } from "@/config/five-product-launch";
import {
	CUSTOM_PEPTIDE_SLUG,
	getPeptideBySlug,
	getPeptideCalculatorMode,
	getPeptideName,
} from "@/lib/calculator/peptide-data";
import { calcReverse, calculateAll } from "@/lib/calculator/calculations";
import { useCalculatorState } from "@/lib/calculator/use-calculator-state";
import type { Locale } from "@/config/locale";
import type { BuyBridgeProduct } from "@/lib/calculator/buy-bridge";
import { getActiveBuyBridge, getInStockBuyBridgeSlugs } from "@/lib/calculator/buy-bridge-data";
import type { SavedProtocolRecord } from "@/lib/calculator/types";
import { useBuyBridgeData } from "./buy-bridge-context";
import { BrazilLanguageSuggestion } from "@/ui/components/locale-channel/brazil-language-suggestion";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Tab = "calculator" | "titration" | "protocols";

interface PeptideCalculatorClientProps {
	channel: string;
	locale: string;
	initialSlug?: string;
	lockedPeptide?: boolean;
}

// id shared by the Cockpit wrapper, the StickyPill IntersectionObserver, and the
// pill's smooth-scroll target. Single source so the three never drift.
const COCKPIT_ID = "pr-cockpit";

function BuyBridgeLoadingSkeleton() {
	return (
		<div
			className="bg-muted/30 mt-4 min-h-64 animate-pulse rounded-2xl border border-border"
			aria-hidden="true"
			data-testid="buy-bridge-loading"
		/>
	);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PeptideCalculatorClient({
	channel,
	locale,
	initialSlug,
	lockedPeptide = false,
}: PeptideCalculatorClientProps) {
	const t = useTranslations("peptideCalculator");

	const [activeTab, setActiveTab] = useState<Tab>("calculator");

	// Single live source of truth for all calculator input state + the derived result.
	const calc = useCalculatorState({ initialSlug, lockedPeptide, locale, channel });
	const { state, result } = calc;
	const currentPeptide = getPeptideBySlug(state.peptideSlug);
	const hasNeutralResearchUi =
		getPeptideCalculatorMode(currentPeptide) === "research-dilution" ||
		isFiveProductStudyContextSlug(state.peptideSlug);
	// Research-dilution entries have no titration/protocol surface. Derive the visible
	// tab instead of synchronizing state in an effect, so switching from a dose peptide
	// while another tab is open can never leave the calculator blank.
	const visibleTab = hasNeutralResearchUi ? "calculator" : activeTab;

	// Buy-bridge data injected by the Suspense-quarantined server loader (via
	// BuyBridgeDataBridge → context). `null` until the loader streams in. A context update
	// only changes props on the existing children; PeptidePicker (and the whole calculator)
	// stays mounted, so an open modal or typed input is never discarded.
	const buyBridgeData = useBuyBridgeData();

	// Reverse-mode cockpit: the result-first cockpit + sticky pill must reflect the
	// water volume the mixing-helper DERIVED, not the forward bacWater the user last
	// set. We compute an effective result with the reverse BAC WITHOUT mutating
	// state.bacWater, so the forward value survives a mode switch. Forward mode keeps
	// the hook's memoized `result` untouched (analytics read the same `result`).
	const displayResult = useMemo(() => {
		if (hasNeutralResearchUi || state.mode !== "reverse") return result;
		const rev = calcReverse({ vial: state.peptideAmount, dose: state.dose, profile: state.reverseProfile });
		const effectiveBac = rev?.bac ?? state.bacWater;
		return calculateAll({
			peptideAmount: state.peptideAmount,
			bacWater: effectiveBac,
			dose: state.dose,
			syringeSize: state.syringeSize,
			calibration: state.calibration,
		});
	}, [
		state.mode,
		state.peptideAmount,
		state.dose,
		state.reverseProfile,
		state.bacWater,
		state.syringeSize,
		state.calibration,
		hasNeutralResearchUi,
		result,
	]);

	// calculator_opened — one-shot. Mirror the CalculatorForm default (retatrutide)
	// so analytics reflect the peptide actually shown on the hub when no initialSlug
	// is passed. (Fires on mount before any restore, exactly like before.)
	const openedRef = useRef(false);
	useEffect(() => {
		if (!openedRef.current) {
			openedRef.current = true;
			posthog.capture("calculator_opened", {
				peptide_slug: initialSlug ?? "retatrutide",
				bridge_version: "calculator_bridge_v2",
				locale,
				channel,
			});
		}
	}, [initialSlug, locale, channel]);

	// calculator_result — debounced 800ms on every value/result change, only when a
	// real volume exists. Same event name + payload fields as the prior
	// onValuesChange-driven capture; now driven directly from hook state.
	const resultDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		if (resultDebounceRef.current) clearTimeout(resultDebounceRef.current);
		resultDebounceRef.current = setTimeout(() => {
			if (result.volume > 0) {
				posthog.capture("calculator_result", {
					bridge_version: "calculator_bridge_v2",
					locale,
					channel,
					peptide_slug: state.peptideSlug,
					dose_mg: state.dose,
					insulin_units: Math.round(result.insulinUnits),
					doses_per_vial: result.dosesPerVial,
				});
			}
		}, 800);
		return () => {
			if (resultDebounceRef.current) clearTimeout(resultDebounceRef.current);
		};
	}, [
		state.peptideSlug,
		state.dose,
		result.volume,
		result.insulinUnits,
		result.dosesPerVial,
		locale,
		channel,
	]);

	// Loading a saved protocol switches to the tab that owns its kind: a titration plan
	// opens the Titrationsplaner; a single protocol opens the calculator. (Re-populating
	// the inputs is the existing follow-up; this fixes the tab routing for titration.)
	const handleLoadProtocol = useCallback((protocol: SavedProtocolRecord) => {
		setActiveTab(protocol.kind === "titration" ? "titration" : "calculator");
	}, []);

	const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
		{
			id: "calculator",
			label: t("tabs.calculator"),
			icon: <Calculator className="h-4 w-4" />,
		},
		{
			id: "titration",
			label: t("tabs.titration"),
			icon: <CalendarDays className="h-4 w-4" />,
		},
		{
			id: "protocols",
			label: t("tabs.protocols"),
			icon: <BookmarkPlus className="h-4 w-4" />,
		},
	];

	// Snapshot for SavedProtocols (no `result` needed; matches its CurrentValues shape).
	const protocolValues = {
		peptideSlug: state.peptideSlug,
		peptideAmount: state.peptideAmount,
		bacWater: state.bacWater,
		dose: state.dose,
		syringeSize: state.syringeSize,
	};

	const accentClass = currentPeptide ? accentClassFor(currentPeptide, locale as Locale) : "text-primary";

	// Authoritative IN-STOCK slug set for the picker's "Auf Lager" dot/badge. Sourced
	// from the Saleor-resolved buy-bridge map (hub) — ONLY slugs whose product is
	// actually in stock right now (inStock === true), NOT merely the sellable map keys
	// (which include out-of-stock-but-listed products). Undefined on spokes (no map) →
	// the picker shows NO in-stock dot (the picker is locked there anyway). semaglutid
	// is never in the map → never "Auf Lager".
	const inStockSlugs = useMemo(() => getInStockBuyBridgeSlugs(buyBridgeData), [buyBridgeData]);

	// Restoring a history chip: set the peptide FIRST (resets vial/bac/dose to that
	// peptide's defaults), then overlay the stored amounts so the reducer lands on the
	// exact recalled state. Mode is restored too. Batched in one handler.
	const handleRestoreHistory = useCallback(
		(entry: HistoryStripRestore) => {
			if (entry.peptideSlug !== state.peptideSlug) {
				calc.setPeptideSlug(entry.peptideSlug);
			}
			calc.setPeptideAmount(entry.peptideAmount);
			calc.setBacWater(entry.bacWater);
			calc.setDose(entry.dose);
			calc.setMode(entry.mode === "reverse" ? "reverse" : "forward");
			if (typeof window !== "undefined") {
				window.scrollTo({ top: 0, behavior: "smooth" });
			}
		},
		[calc, state.peptideSlug],
	);

	// Snapshot for the Actions (Save/Share). Peptide name resolved for the guest
	// protocol label + share-sheet context.
	const actionsValues = {
		peptideSlug: state.peptideSlug,
		peptideName: currentPeptide ? getPeptideName(currentPeptide, locale as Locale) : state.peptideSlug,
		peptideAmount: state.peptideAmount,
		bacWater: state.bacWater,
		dose: state.dose,
		syringeSize: state.syringeSize,
		displayUnit: state.displayUnit,
	};

	// Buy-bridge product for the CURRENTLY selected peptide. Once the server promise resolves,
	// the hub map follows the picker; a locked spoke falls back to its single product.
	const activeBuyBridge: BuyBridgeProduct = getActiveBuyBridge(buyBridgeData, state.peptideSlug);

	// €/Dosis label for the cockpit's green tile — reference currency, shared formatter with
	// the buy card's spec strip (so the two never disagree). Null unless an EXACT vial match.
	const pricePerDoseLabel =
		!hasNeutralResearchUi && activeBuyBridge
			? (computeBuyBridgePerDoseLabel(activeBuyBridge, state.peptideAmount, result.dosesPerVial, locale) ??
				undefined)
			: undefined;

	return (
		<div>
			{locale === "pt" && <BrazilLanguageSuggestion calculatorState={state} />}
			{/* Tab bar — responsive segmented control */}
			{!hasNeutralResearchUi && (
				<div
					className="bg-secondary/30 mb-4 inline-flex rounded-lg p-1 sm:mb-6"
					role="tablist"
					aria-label={t("tabs.ariaLabel")}
				>
					{tabs.map((tab) => (
						<button
							key={tab.id}
							type="button"
							role="tab"
							id={`calc-tab-${tab.id}`}
							aria-selected={activeTab === tab.id}
							aria-controls="calc-tabpanel"
							tabIndex={activeTab === tab.id ? 0 : -1}
							title={tab.label}
							onClick={() => setActiveTab(tab.id)}
							aria-label={tab.label}
							className={[
								"flex min-h-10 min-w-10 items-center justify-center gap-1.5 rounded-md px-2.5 py-2 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:min-h-0 sm:min-w-0 sm:px-3 sm:py-1.5",
								activeTab === tab.id
									? "bg-card text-foreground shadow-sm"
									: "text-muted-foreground hover:text-foreground",
							].join(" ")}
						>
							{tab.icon}
							<span className="hidden sm:inline">{tab.label}</span>
						</button>
					))}
				</div>
			)}

			<div
				role="tabpanel"
				id="calc-tabpanel"
				tabIndex={0}
				aria-labelledby={hasNeutralResearchUi ? undefined : `calc-tab-${visibleTab}`}
			>
				{visibleTab === "calculator" && (
					<>
						{/*
						 * Sticky result-rail layout. The cockpit, buy card and actions form ONE
						 * pinned rail on desktop so the result can never slide over the buy card
						 * (the prior bug: the cockpit was a lone `lg:sticky` cell inside the tall
						 * grid, so its containing block was the whole grid and it drifted down over
						 * the cells below it).
						 *
						 * Mobile (<lg): a single flex column in the UX-critical order — mode →
						 * picker → COCKPIT (result first!) → inputs → safety → buy → actions. The
						 * right-rail wrapper is `display:contents` here, so its three children
						 * (cockpit/buy/actions) flow into THIS flex container and obey their own
						 * `order-*`, interleaving with inputs + safety. Nothing is sticky on mobile
						 * (the StickyPill owns the thumb zone).
						 *
						 * Desktop (lg ~880px+): a 2-column grid. `mode` + `pick` span both columns
						 * (rows 1–2). Row 3 left = inputs + safety; row 3 right = the rail wrapper,
						 * which becomes a real `lg:block lg:sticky lg:top-[84px] lg:self-start` box
						 * containing cockpit → buy → actions stacked in normal flow. The whole rail
						 * pins together at top:84 while the taller inputs column scrolls, then
						 * releases when that column ends. No overlap: buy/actions are in the same
						 * flow as the cockpit INSIDE the sticky box.
						 *
						 * z/top map unchanged: header 0 → tabnav 60 → sticky rail top-84 → mobile
						 * pill bottom-14.
						 */}
						<div className="mx-auto flex w-full max-w-2xl flex-col gap-4 sm:gap-6 lg:mx-0 lg:grid lg:max-w-none lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start lg:gap-x-8">
							{/*
							 * Single-column band (phones + portrait tablets, < lg) is centered and
							 * capped at a comfortable reading width (max-w-2xl) so a portrait tablet
							 * (768-834px) reads as an intentional focused tool, not a stretched phone.
							 * At lg the cap is removed (lg:max-w-none, lg:mx-0) and the layout becomes
							 * the full-width 2-column sticky result rail.
							 */}
							{/* mode — forward/reverse toggle, full width above the picker */}
							{!hasNeutralResearchUi && (
								<div className="order-1 lg:col-span-2 lg:col-start-1 lg:row-start-1">
									<ModeToggle mode={state.mode} onModeChange={calc.setMode} />
								</div>
							)}

							{/* pick — peptide picker card (opens the PickerModal), full width
							    above the grid */}
							<div
								className={`order-2 lg:col-span-2 lg:col-start-1 ${hasNeutralResearchUi ? "lg:row-start-1" : "lg:row-start-2"}`}
							>
								<PeptidePicker
									selectedSlug={state.peptideSlug}
									onChange={calc.setPeptideSlug}
									locale={locale}
									locked={lockedPeptide}
									inStockSlugs={inStockSlugs}
								/>
							</div>

							{/*
							 * LEFT column wrapper — ONE continuous flow cell. Mobile: `contents`,
							 * so the three children rejoin the parent flex and keep their own
							 * `order-*` (inputs 4, safety 5, history 8), interleaving with the
							 * cockpit/buy/actions exactly as before. Desktop: a flex-col pinned to
							 * col-start-1/row-start-3 so inputs → safety → history flow continuously
							 * with a clean `gap-6` and NO dead mid-gap — the wrapper is decoupled
							 * from the (now single-row) right rail's height, so the left column no
							 * longer inherits the tall rail's stretched rows.
							 */}
							<div
								className={`contents lg:col-start-1 lg:flex lg:flex-col lg:gap-6 ${hasNeutralResearchUi ? "lg:row-start-2" : "lg:row-start-3"}`}
							>
								{/* inputs — top of the left column on desktop, after the cockpit on mobile */}
								<div className="order-4 lg:order-none">
									<CalculatorForm
										state={state}
										locale={locale}
										onPeptideAmountChange={calc.setPeptideAmount}
										onBacWaterChange={calc.setBacWater}
										onDoseChange={calc.setDose}
										onSyringeSizeChange={calc.setSyringeSize}
										onDoseUnitModeChange={calc.setDoseUnitMode}
										onReverseProfileChange={calc.setReverseProfile}
									/>
								</div>

								{/* safety — under the inputs (desktop spacing from the wrapper's
								    flex gap; mobile from the parent flex gap). */}
								{!hasNeutralResearchUi && (
									<div className="order-5 lg:order-none">
										<SafetyNotes
											state={state}
											peptide={currentPeptide}
											result={displayResult}
											locale={locale}
											onDoseChange={calc.setDose}
											onBacWaterChange={calc.setBacWater}
											onCalibrationChange={calc.setCalibration}
										/>
									</div>
								)}

								{/*
								 * history — "Zuletzt berechnet" strip. Reads localStorage via
								 * useSyncExternalStore; re-snapshots on the hook's debounced write
								 * (HISTORY_EVENT); hides itself when empty.
								 *
								 * On MOBILE it stays the last block in the flex column (order-8, full
								 * width, after actions). On DESKTOP it is the bottom of this continuous
								 * left-column flow — directly under SafetyNotes with the wrapper's
								 * gap-6 spacing, so there is no mid-gap above it anymore.
								 */}
								{!hasNeutralResearchUi && (
									<div className="order-8 lg:order-none">
										<HistoryStrip onRestore={handleRestoreHistory} />
									</div>
								)}
							</div>

							{/*
							 * Right rail wrapper. Mobile: `contents` → children join the parent
							 * flex and obey their own `order` (cockpit order-3, buy order-6, actions
							 * order-7) so the result still comes first and buy/actions come last.
							 * Desktop: ONE sticky box in a SINGLE cell (col-start-2/row-start-3),
							 * stacking cockpit → buy → actions in normal flow. It no longer spans
							 * rows — the left column is now its own continuous cell — so the rail's
							 * height can never stretch the left items and re-open a mid-gap. Pins as
							 * a unit at top:84.
							 */}
							<div
								className={`contents lg:sticky lg:top-[84px] lg:col-start-2 lg:flex lg:flex-col lg:gap-6 lg:self-start ${hasNeutralResearchUi ? "lg:row-start-2" : "lg:row-start-3"}`}
							>
								{/* result — Cockpit. Result-first on mobile (order-3, before inputs). */}
								<div id={COCKPIT_ID} className="order-3 lg:order-none">
									{currentPeptide && (
										<Cockpit
											state={state}
											result={displayResult}
											peptide={currentPeptide}
											locale={locale}
											displayUnit={state.displayUnit}
											onDisplayUnitChange={calc.setDisplayUnit}
											pricePerDoseLabel={pricePerDoseLabel}
											productLink={
												!hasNeutralResearchUi && displayResult.volume > 0 ? (
													<ResultProductLink
														product={activeBuyBridge}
														peptideAmount={state.peptideAmount}
														locale={locale}
														channel={channel}
													/>
												) : null
											}
										/>
									)}
								</div>

								{/* buy — buy-bridge card / fallback. Inside the sticky rail on desktop;
								    after inputs + safety on mobile. Height reserved to avoid CLS. */}
								<div className="order-6 min-h-[1px] lg:order-none">
									{(() => {
										// RUNTIME availability gate for the prominent buy button.
										//
										// This used to read `resolveBuyBridgeProductSlug(state.peptideSlug)`, a purely
										// STATIC allow-list: it happily rendered the primary CTA for a peptide whose
										// Saleor product had been delisted, and the PDP then answered 200 with a
										// `noindex` meta tag — a user dead end (not a 404).
										//
										// `activeBuyBridge` is the same product the buy card uses: resolved from Saleor
										// at request time for THIS channel, so it is non-null only when the product
										// genuinely exists and is sellable here. The `calcSlug` check keeps a stale hub
										// map entry from being attributed to the peptide the picker just moved to.
										//
										// Liveness — not stock — is the right gate for THIS button: a live-but-sold-out
										// SKU still has a working PDP with the back-in-stock form, so linking it is not
										// a dead end. Real stock is consumed one level down (BuyBridgeCard's OOS state,
										// `inStockSlugs` for the picker), which is where the channel-specific in-stock
										// signal belongs.
										const liveProduct =
											activeBuyBridge && activeBuyBridge.calcSlug === state.peptideSlug
												? activeBuyBridge
												: null;
										const peptide = getPeptideBySlug(state.peptideSlug);
										const peptideName = peptide ? getPeptideName(peptide, locale as Locale) : null;

										// The learn-more target below is the calculator SPOKE. A spoke exists for
										// every PEPTIDE_DATA slug EXCEPT the hub-only "custom" pseudo-entry, whose
										// route calls `notFound()` (`generateStaticParams` uses `getAllPeptideSlugs`,
										// which filters it out). "custom" is not reachable through the picker modal,
										// but a shared link CAN put it into state: `?p=custom` passes the share-param
										// validation because `getPeptideBySlug("custom")` resolves. Guard the slug so
										// that link is never built.
										const hasSpokePage = peptide !== undefined && peptide.slug !== CUSTOM_PEPTIDE_SLUG;

										const trackFallbackCta = () =>
											posthog.capture("calculator_cta_clicked", {
												cta_source: "fallback",
												bridge_version: "calculator_bridge_v2",
												locale,
												product_slug: liveProduct?.productSlug ?? null,
												peptide_slug: state.peptideSlug,
												channel,
											});

										// Non-commercial link; tracked separately so learn-more clicks stop inflating
										// the commercial "fallback" bucket they used to share.
										const trackLearnMore = () =>
											posthog.capture("calculator_cta_clicked", {
												cta_source: "learn_more",
												bridge_version: "calculator_bridge_v2",
												locale,
												peptide_slug: state.peptideSlug,
												channel,
											});

										// Prominent product button (mirrors the buy-bridge primary CTA) only when the
										// product is live right now; otherwise the subtle learn-more text link.
										const fallback =
											liveProduct && peptideName ? (
												<div className="mt-4">
													<InternalSourceLink
														href={`/${locale}/${channel}/products/${liveProduct.productSlug}`}
														source="calculator"
														detail={state.peptideSlug}
														onClick={trackFallbackCta}
														className="hover:bg-primary/90 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors sm:w-auto"
													>
														{t("spoke.cta.viewPeptide", { name: peptideName })} →
													</InternalSourceLink>
												</div>
											) : isFiveProductLaunchSlug(state.peptideSlug) ||
											  lockedPeptide ||
											  !hasSpokePage ? null : (
												// Non-commercial fallback. It used to point at
												// `/products/<calculator slug>`, which does not exist for the
												// calculator-only peptides (dsip, sermorelin, hexarelin, ghrp-2,
												// ghrp-6, n-acetyl-semax, 5-amino-1mq, semaglutid,
												// hgh-fragment-176-191, …) — those URLs answer 200 + noindex, so the
												// link was a dead end. The calculator SPOKE exists for every peptide
												// in PEPTIDE_DATA (`generateStaticParams` derives from
												// `getAllPeptideSlugs`), so it is the correct "learn more" target.
												// On a locked spoke we are already on that page → render nothing
												// rather than a self-link. Clean href: no page reads an attribution
												// on a spoke, and crawlers must not discover parameter URLs.
												<div className="mt-4 text-center">
													<Link
														href={`/${locale}/${channel}/peptide-calculator/${state.peptideSlug}`}
														className="text-sm text-primary underline-offset-4 hover:underline"
														onClick={trackLearnMore}
													>
														{t("spoke.cta.learnMore")} →
													</Link>
												</div>
											);

										// The small slot waits for the context-injected server data (skeleton until
										// the quarantined loader streams in — same visual as the former Suspense
										// fallback). Non-live peptides resolve to the existing fallback without ever
										// replacing the calculator component.
										if (hasNeutralResearchUi) return fallback;
										if (buyBridgeData === null) return <BuyBridgeLoadingSkeleton />;
										return activeBuyBridge ? (
											<BuyBridgeGate
												product={activeBuyBridge}
												peptideAmount={state.peptideAmount}
												dosesPerVial={result.dosesPerVial}
												locale={locale}
												channel={channel}
												fallback={fallback}
												disableMobileSticky
											/>
										) : (
											fallback
										);
									})()}
								</div>

								{/* act — actions (Save / Share). Bottom of the sticky rail on desktop;
								    after the buy card on mobile. */}
								{!hasNeutralResearchUi && (
									<div className="order-7 lg:order-none">
										<CalculatorActions values={actionsValues} />
									</div>
								)}
							</div>
						</div>

						{/* Mobile-only bottom pill — keeps the live result glued to the thumb
						    zone once the Cockpit scrolls off the top. Desktop uses the sticky
						    cockpit instead, so the pill is `lg:hidden` internally. */}
						{!hasNeutralResearchUi && (
							<StickyPill
								cockpitId={COCKPIT_ID}
								insulinUnits={displayResult.insulinUnits}
								volume={displayResult.volume}
								locale={locale}
								accentClassName={accentClass}
								// Literal "live" mirrors the Cockpit's COPY.live; cockpit i18n
								// extraction (peptideCalculator.cockpit.*) is the separate task T17.
								liveLabel="live"
							/>
						)}
					</>
				)}

				{/* Titration + protocols mirror the calculator's tablet treatment: centered,
				    reading-width-capped on phones + portrait tablets; the planner's own 2-col
				    inputs grid still expands at lg (lg:max-w-none). */}
				{visibleTab === "titration" && (
					<div className="mx-auto w-full max-w-3xl lg:mx-0 lg:max-w-none">
						<TitrationPlanner locale={locale} initialSlug={initialSlug} lockedPeptide={lockedPeptide} />
					</div>
				)}

				{visibleTab === "protocols" && (
					<div className="mx-auto w-full max-w-3xl lg:mx-0 lg:max-w-none">
						<SavedProtocols
							locale={locale}
							channel={channel}
							currentValues={protocolValues}
							onLoadProtocol={handleLoadProtocol}
						/>
					</div>
				)}
			</div>
		</div>
	);
}
